/**
 * CliniKit – Clinic Types Configuration (Single Source of Truth)
 *
 * All clinic types, sub-specializations, icons, and labels are defined here.
 * Import from this file in any component that needs clinic type data.
 */
import {
  ChatCircle,
  Hand,
  Barbell,
  Brain,
  Palette,
  HandHeart,
  UsersThree,
  Target,
  HeartHalf,
  ForkKnife,
  Leaf,
  FlowerLotus,
  Stethoscope,
  Sparkle,
  DotsThreeCircle,
} from "@phosphor-icons/react";

// ============================================
// CLINIC TYPES
// ============================================

export const CLINIC_TYPES = [
  { value: "speech_therapist", label: "קלינאות תקשורת", icon: ChatCircle },
  { value: "occupational_therapist", label: "ריפוי בעיסוק", icon: Hand },
  { value: "physiotherapy", label: "פיזיותרפיה", icon: Barbell },
  { value: "psychology", label: "פסיכולוגיה ופסיכותרפיה", icon: Brain },
  { value: "emotional_therapy", label: "טיפול רגשי", icon: Palette },
  { value: "clinical_social_work", label: "עבודה סוציאלית קלינית", icon: HandHeart },
  { value: "parenting_counseling", label: "הדרכת הורים וייעוץ משפחתי", icon: UsersThree },
  { value: "personal_coaching", label: "אימון אישי", icon: Target },
  { value: "couples_counseling", label: "ייעוץ זוגי ומיני", icon: HeartHalf },
  { value: "nutrition", label: "תזונה ודיאטה", icon: ForkKnife },
  { value: "naturopathy", label: "נטורופתיה ורפואה טבעית", icon: Leaf },
  { value: "chinese_medicine", label: "רפואה סינית ומשלימה", icon: FlowerLotus },
  { value: "gynecology", label: "גינקולוגיה", icon: Stethoscope },
  { value: "aesthetics", label: "קליניקה לאסתטיקה", icon: Sparkle },
  { value: "other", label: "אחר", icon: DotsThreeCircle },
];

/**
 * Clinic types whose business is mostly one-off / walk-in clients.
 * At signup the "לקוחות מזדמנים" toggle is pre-checked for these
 * (user can still turn it off; Settings behaves as before).
 */
export const TEMP_CUSTOMERS_DEFAULT_ON_TYPES = ["aesthetics"];

export const isTempCustomersDefaultOn = (clinicType) =>
  TEMP_CUSTOMERS_DEFAULT_ON_TYPES.includes(clinicType);

export const ALL_CLINIC_TYPE_CODES = CLINIC_TYPES.map((t) => t.value);

export const getClinicTypeLabel = (code) =>
  CLINIC_TYPES.find((t) => t.value === code)?.label || code;

// ============================================
// SUB-SPECIALIZATIONS BY CLINIC TYPE
// ============================================

export const SUB_SPECIALIZATIONS_BY_TYPE = {
  speech_therapist: [
    { value: "stuttering", label: "גמגום" },
    { value: "articulation", label: "היגוי ומובנות דיבור" },
    { value: "language_delay", label: "עיכוב שפתי" },
    { value: "tongue_thrust", label: "דחיקת לשון" },
    { value: "autism", label: "אוטיזם (ASD)" },
    { value: "swallowing", label: "קשיי בליעה" },
    { value: "voice", label: "הפרעות קול" },
    { value: "fluency", label: "שטף דיבור" },
    { value: "aac", label: "תקשורת חלופית" },
    { value: "aphasia", label: "אפזיה" },
    { value: "apraxia", label: "אפרקסיה" },
    { value: "cleft", label: "חך שסוע" },
    { value: "hearing", label: "לקויות שמיעה" },
    { value: "oral_functions", label: "תפקודי פה" },
    { value: "dysarthria", label: "דיסארטריה" },
  ],
  occupational_therapist: [
    { value: "child_development", label: "התפתחות הילד" },
    { value: "sensory_regulation", label: "ויסות חושי" },
    { value: "writing_fine_motor", label: "כתיבה ומוטוריקה עדינה" },
    { value: "neurological_rehab", label: "שיקום נוירולוגי" },
    { value: "geriatrics", label: "גריאטריה" },
  ],
  physiotherapy: [
    { value: "sports_injuries", label: "פציעות ספורט" },
    { value: "orthopedic_rehab", label: "שיקום אורתופדי" },
    { value: "pelvic_floor", label: "רצפת אגן" },
    { value: "respiratory", label: "פיזיותרפיה נשימתית" },
    { value: "child_development", label: "התפתחות הילד" },
  ],
  psychology: [
    { value: "clinical", label: "קלינית (מבוגרים/ילדים)" },
    { value: "rehabilitative", label: "שיקומית" },
    { value: "educational", label: "חינוכית" },
    { value: "cbt", label: "CBT" },
    { value: "licbt", label: "LICBT" },
    { value: "dbt", label: "DBT" },
  ],
  emotional_therapy: [
    { value: "art_therapy", label: "טיפול באמנות" },
    { value: "music_therapy", label: "טיפול במוזיקה" },
    { value: "psychodrama", label: "פסיכודרמה" },
    { value: "animal_assisted", label: "טיפול בעזרת בעלי חיים" },
    { value: "therapeutic_riding", label: "רכיבה טיפולית" },
  ],
  clinical_social_work: [
    { value: "trauma", label: "טראומה" },
    { value: "addiction", label: "התמכרויות" },
    { value: "community_mental_health", label: "בריאות הנפש בקהילה" },
  ],
  parenting_counseling: [
    { value: "sleep_consulting", label: "ייעוץ שינה" },
    { value: "weaning", label: "גמילה" },
    { value: "adhd", label: "הפרעות קשב (ADHD)" },
    { value: "adolescence", label: "גיל ההתבגרות" },
    { value: "regulation_difficulties", label: "קשיי ויסות" },
  ],
  personal_coaching: [
    { value: "business_coaching", label: "אימון עסקי" },
    { value: "relationship_coaching", label: "אימון למציאת זוגיות" },
    { value: "career_coaching", label: "אימון לקריירה" },
    { value: "time_management", label: "אימון לניהול זמן" },
  ],
  couples_counseling: [
    { value: "family_mediation", label: "גישור משפחתי" },
    { value: "premarital", label: "ייעוץ לפני נישואין" },
    { value: "sexual_therapy", label: "טיפול מיני" },
  ],
  nutrition: [
    { value: "clinical_diet", label: "דיאטה קלינית (סוכרת/לב)" },
    { value: "sports_nutrition", label: "תזונת ספורט" },
    { value: "eating_disorders", label: "הפרעות אכילה" },
    { value: "child_nutrition", label: "תזונה לילדים" },
  ],
  naturopathy: [
    { value: "herbal_medicine", label: "צמחי מרפא" },
    { value: "iridology", label: "אירידיאולוגיה (אבחון בקשתית העין)" },
    { value: "bach_flowers", label: "פרחי באך" },
  ],
  chinese_medicine: [
    { value: "acupuncture", label: "דיקור סיני (Acupuncture)" },
    { value: "shiatsu", label: "שיאצו" },
    { value: "tuina", label: "טווינא" },
    { value: "reflexology", label: "רפלקסולוגיה" },
  ],
  gynecology: [],
  aesthetics: [
    { value: "facial_treatments", label: "טיפולי פנים" },
    { value: "cosmetics_makeup", label: "קוסמטיקה ואיפור" },
    { value: "permanent_makeup", label: "איפור קבוע / מיקרובליידינג" },
    { value: "eyebrows", label: "עיצוב גבות" },
    { value: "lashes", label: "הרמת והארכת ריסים" },
    { value: "nails", label: "ציפורניים (מניקור / פדיקור)" },
    { value: "laser_hair_removal", label: "הסרת שיער בלייזר" },
    { value: "waxing", label: "הסרת שיער בשעווה / שוגרינג" },
    { value: "skin_rejuvenation", label: "חידוש וטיפוח עור" },
    { value: "anti_aging", label: "אנטי-אייג׳ינג ומיצוק" },
    { value: "acne_treatment", label: "טיפולי אקנה ופיגמנטציה" },
    { value: "peeling", label: "פילינג כימי / מכשירים" },
    { value: "microneedling", label: "מיקרונידלינג / מזותרפיה" },
    { value: "body_contouring", label: "עיצוב גוף וצלוליט" },
    { value: "massage_spa", label: "עיסוי וספא" },
    { value: "medical_cosmetics", label: "קוסמטיקה רפואית / פרא-מדיקלית" },
    { value: "injectables", label: "הזרקות (בוטוקס / חומצה היאלורונית)" },
    { value: "hair_scalp", label: "שיער וקרקפת" },
    { value: "tanning", label: "שיזוף בהתזה" },
    { value: "bridal", label: "טיפולי כלות ואירועים" },
  ],
  other: [],
};

/**
 * Returns the set of predefined sub-specialization value strings for a clinic type.
 * Used to detect custom (user-added) sub-specializations.
 */
export const getPredefinedSubValues = (clinicType) => {
  const subs = SUB_SPECIALIZATIONS_BY_TYPE[clinicType] || [];
  return new Set(subs.map((s) => s.value));
};

// ============================================
// AGE RANGE OPTIONS (shared across all types)
// ============================================

export const AGE_RANGE_OPTIONS = [
  { value: "infants", label: "תינוקות (0-2)" },
  { value: "toddlers", label: "פעוטות (2-4)" },
  { value: "preschool", label: "גן (4-6)" },
  { value: "children", label: "ילדים (6-12)" },
  { value: "teens", label: "מתבגרים (12-18)" },
  { value: "adults", label: "מבוגרים (18+)" },
  { value: "elderly", label: "קשישים (65+)" },
];

// ============================================
// MEETING DURATIONS (shared across all types)
// ============================================

export const MEETING_DURATIONS = [
  { value: 15, label: "15 דק׳" },
  { value: 30, label: "30 דק׳" },
  { value: 45, label: "45 דק׳" },
  { value: 60, label: "60 דק׳" },
  { value: "custom", label: "מותאם" },
];

// ============================================
// SUGGESTED TREATMENT TYPES (signup wizard, per clinic type)
// 1–3 per type, pre-added as "suggested" rows the therapist can remove.
// Intentionally excludes a plain "טיפול" — that is the general/default type.
// Rate is inherited from the clinic default (saved as null) unless edited.
// ============================================

export const SUGGESTED_TREATMENT_TYPES_BY_CLINIC_TYPE = {
  speech_therapist: [
    { name: "אבחון", duration: 60 },
    { name: "הדרכת הורים", duration: 45 },
  ],
  occupational_therapist: [
    { name: "אבחון", duration: 60 },
    { name: "הדרכת הורים", duration: 45 },
  ],
  physiotherapy: [
    { name: "אבחון ראשוני", duration: 60 },
    { name: "טיפול המשך", duration: 45 },
  ],
  psychology: [
    { name: "פגישת היכרות", duration: 50 },
    { name: "טיפול פרטני", duration: 50 },
    { name: "טיפול זוגי", duration: 60 },
  ],
  emotional_therapy: [
    { name: "פגישת היכרות", duration: 50 },
    { name: "טיפול פרטני", duration: 50 },
    { name: "הדרכת הורים", duration: 50 },
  ],
  clinical_social_work: [
    { name: "פגישת אינטייק", duration: 50 },
    { name: "טיפול פרטני", duration: 50 },
  ],
  parenting_counseling: [
    { name: "פגישת היכרות", duration: 60 },
    { name: "הדרכת הורים", duration: 50 },
  ],
  personal_coaching: [
    { name: "פגישת היכרות", duration: 60 },
    { name: "מפגש אימון", duration: 60 },
  ],
  couples_counseling: [
    { name: "פגישת היכרות", duration: 60 },
    { name: "טיפול זוגי", duration: 60 },
    { name: "טיפול פרטני", duration: 50 },
  ],
  nutrition: [
    { name: "פגישה ראשונה", duration: 60 },
    { name: "פגישת מעקב", duration: 30 },
  ],
  naturopathy: [
    { name: "אבחון ראשוני", duration: 60 },
    { name: "טיפול המשך", duration: 45 },
  ],
  chinese_medicine: [
    { name: "פגישה ראשונה", duration: 60 },
    { name: "טיפול דיקור", duration: 45 },
  ],
  gynecology: [
    { name: "ביקור ראשון", duration: 30 },
    { name: "ביקור מעקב", duration: 20 },
  ],
  aesthetics: [
    { name: "ייעוץ ראשוני", duration: 30 },
    { name: "טיפול פנים", duration: 60 },
    { name: "הסרת שיער", duration: 30 },
  ],
  other: [],
};

export const getSuggestedTreatmentTypes = (clinicType) =>
  SUGGESTED_TREATMENT_TYPES_BY_CLINIC_TYPE[clinicType] || [];

// ============================================
// VAT TYPES (shared across all types)
// ============================================

export const VAT_TYPES = [
  { value: "exempt", label: "עוסק פטור" },
  { value: "licensed", label: "עוסק מורשה" },
  { value: "company", label: 'חברה בע"מ' },
];
